/**
 * Platform-free helpers shared by sentry.web.ts and sentry.native.ts:
 * PII scrubbing and the breadcrumb ring buffer.
 *
 * This file must not import from `react-native` or touch `window` — it is
 * bundled on every platform. Anything platform-specific belongs in the
 * .web.ts / .native.ts implementations.
 *
 * Why scrubbing matters more here than in most apps: Cynosure is E2EE-mandatory.
 * Telemetry leaves the device and lands in Loki, so anything that makes it into
 * an error message is no longer end-to-end encrypted. The ingest side applies
 * the same email mask as a backstop (see the loki.process stage in a3rn-fleet's
 * alloy-ingest.yaml), but that is a net, not the plan — the plan is not to send
 * it in the first place.
 */

export interface Breadcrumb {
  category: string;
  message: string;
  level?: 'debug' | 'info' | 'warning' | 'error' | 'fatal';
  data?: Record<string, unknown>;
  timestamp?: number;
}

// Mirrors _EMAIL_RE in backend/app/logging_config.py so client and backend logs
// mask identically — a support question that spans both should not show the
// address one way in one stream and another way in the other.
const EMAIL_RE = /[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/g;

/** `user@example.com` → `u***@e***.com`, matching the backend's `_mask_email`. */
export function maskEmails(input: string): string {
  return input.replace(EMAIL_RE, (email) => {
    const [local, domain] = email.split('@');
    const parts = domain.split('.');
    const tld = parts.length > 1 ? parts.pop()! : '';
    const host = parts.join('.');
    const maskedLocal = local ? `${local[0]}***` : '***';
    const maskedHost = host ? `${host[0]}***` : '***';
    return `${maskedLocal}@${maskedHost}.${tld}`;
  });
}

// Bounds an accidental blob — a stringified encrypted payload, a base64 key, a
// whole response body — from riding along inside an error message. 2 KB is far
// more than any real message and far less than the 128 KiB the ingest endpoint
// rejects at.
const MAX_STRING = 2048;
const MAX_DEPTH = 4;

/**
 * Recursively mask strings in an arbitrary value. Returns a new value; the
 * input is never mutated (call sites pass live objects).
 */
export function scrub(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    const masked = maskEmails(value);
    return masked.length > MAX_STRING ? `${masked.slice(0, MAX_STRING)}…[truncated]` : masked;
  }
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[depth limit]';
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = scrub(v, depth + 1);
  }
  return out;
}

/**
 * Faro's `context` is a flat map of strings, so nested extras have to be
 * flattened. Non-string leaves are JSON-encoded rather than dropped.
 */
export function toFaroContext(extra?: Record<string, unknown>): Record<string, string> {
  const context: Record<string, string> = {};
  if (!extra) return context;
  for (const [key, raw] of Object.entries(scrub(extra) as Record<string, unknown>)) {
    if (raw === undefined || raw === null) continue;
    context[key] = typeof raw === 'string' ? raw : JSON.stringify(raw);
  }
  return context;
}

// ── Breadcrumb ring buffer ─────────────────────────────────────────────────
//
// Breadcrumbs are attached to errors rather than shipped as their own events.
// That is the classic meaning of the word, and it keeps volume proportional to
// FAILURES instead of to activity — pushing one event per API call would be the
// single largest source of ingest traffic in the fleet, for data nobody queries
// except when something breaks.
const LIMIT = 100;
// Only the tail is attached to an error. Everything older is context nobody
// reads, and it counts against the payload cap.
const ATTACH = 20;

let buffer: Breadcrumb[] = [];

export function pushBreadcrumb(crumb: Breadcrumb): void {
  buffer.push({
    ...crumb,
    message: maskEmails(crumb.message ?? ''),
    data: crumb.data ? (scrub(crumb.data) as Record<string, unknown>) : undefined,
    timestamp: crumb.timestamp ?? Date.now(),
  });
  if (buffer.length > LIMIT) buffer = buffer.slice(-LIMIT);
}

/** The most recent breadcrumbs, oldest first, ready to attach to an error. */
export function recentBreadcrumbs(): Breadcrumb[] {
  return buffer.slice(-ATTACH);
}

export function clearBreadcrumbs(): void {
  buffer = [];
}
